import React from "react";
import styles from "./styles/AssetDashboard.module.css";

/*
  The report's ring chart, shared by Overall Asset Allocation and Income
  Breakdown.

  Stroked arcs rather than filled wedges: the ring's width is the stroke, so the
  hole needs no second shape drawn over it and nothing has to be layered in the
  right order.
*/

const GAP_DEG = 1.6;

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const arcPath = (cx, cy, r, from, to) => {
  const [x1, y1] = polar(cx, cy, r, from);
  const [x2, y2] = polar(cx, cy, r, to);
  return `M ${x1} ${y1} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x2} ${y2}`;
};

function Donut({ slices, size = 190, stroke = 34, label }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - stroke) / 2;
  const total = slices.reduce((sum, s) => sum + s.pct, 0) || 1;

  let cursor = 0;
  return (
    <svg
      className={styles.donut}
      viewBox={`0 0 ${size} ${size}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={label}
    >
      {slices.map((slice) => {
        const sweep = (slice.pct / total) * 360;
        const from = cursor;
        const to = cursor + sweep;
        cursor = to;

        /*
          A slice that is the whole ring is a circle, not an arc.

          The gap exists to separate a slice from its NEIGHBOUR. With one slice
          there is no neighbour, and taking the gap out of it anyway left a
          white notch at twelve o'clock on every 100% ring — an asset holding
          the entire allocation looked like it was missing a sliver of itself.

          It has to be a <circle> rather than a 360° arc: an arc whose two ends
          are the same point is degenerate and SVG draws nothing at all, which
          is what the gap was quietly working around.
        */
        if (sweep >= 360 - 1e-6) {
          return (
            <circle
              key={slice.key}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={slice.color}
              strokeWidth={stroke}
            >
              <title>{`${slice.name} — ${slice.pct}%`}</title>
            </circle>
          );
        }

        // A slice too small to give a gap up is drawn whole, never inverted.
        const gap = sweep > GAP_DEG * 2 ? GAP_DEG : 0;
        return (
          <path
            key={slice.key}
            d={arcPath(cx, cy, r, from + gap / 2, to - gap / 2)}
            fill="none"
            stroke={slice.color}
            strokeWidth={stroke}
          >
            <title>{`${slice.name} — ${slice.pct}%`}</title>
          </path>
        );
      })}
    </svg>
  );
}

export default Donut;
